import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import Iconify from '../../../../Iconify';
import useRealNode from '../../../../../hooks/useRealNode';
import { ModalPanel } from './modal-styled';
import { publish, subscribe, unsubscribe } from '../../../../../utils/events';
import '../../payments.css';

const EVENT_NAME = 'CONEKTA_PAYMENTS:SHOW_MODAL_ERROR'

// Flag global para rastrear si el modal ya está siendo mostrado
let isModalOpen = false;

export const showModalError = ({
  errorMessage = 'Ha ocurrido un error',
  errorTitle = 'No pudimos procesar tu pago',
  errorInstruction = 'Revisa la información de tu tarjeta',
  errorCode = ''
}) => {
  return new Promise((resolve) => {
    publish(EVENT_NAME, {
      resolve,
      errorMessage,
      errorTitle,
      errorInstruction,
      errorCode
    })
  })
}

export const ModalError = () => {
  const node = useRealNode('modal-node-portal');
  const [isOpen, setIsOpen] = useState(false);
  const [errorData, setErrorData] = useState(null);
  const isThisInstanceOpenRef = React.useRef(false);

  useEffect(() => {
    const listener = (event) => {
      // Solo procesar el evento si el modal no está abierto globalmente
      if (isModalOpen) {
        return;
      }
      
      // Marcar el modal como abierto antes de procesarlo
      isModalOpen = true;
      isThisInstanceOpenRef.current = true;
      setErrorData({
        errorCode: event.detail.errorCode,
        errorMessage: event.detail.errorMessage,
        errorTitle: event.detail.errorTitle,
        errorInstruction: event.detail.errorInstruction,
        resolve: event.detail.resolve
      });
      setIsOpen(true);
    };
    
    subscribe(EVENT_NAME, listener);
    
    return () => {
      unsubscribe(EVENT_NAME, listener);
      // Si este componente se desmonta y tenía el modal abierto, resetear el flag
      if (isThisInstanceOpenRef.current) {
        isModalOpen = false;
        isThisInstanceOpenRef.current = false;
      }
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    isThisInstanceOpenRef.current = false;
    // Resetear el flag cuando se cierra el modal
    isModalOpen = false;
  };

  const handleRetry = () => {
    handleClose();
    if (errorData?.resolve) {
      errorData.resolve();
    }
  };

  const stopPropagation = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  if (!isOpen || !node) return null;

  return ReactDOM.createPortal(
    <ModalPanel tabIndex="1">
      <ModalContent onClick={stopPropagation}>
        <CloseButton onClick={handleClose}>
          <Iconify icon="ic:baseline-close" />
        </CloseButton>
        <ErrorContentBox>
          <ErrorHeader>
            <WarningIcon>
              <Iconify icon="material-symbols:warning-rounded" size={24} />
            </WarningIcon>
            <ErrorTitle>{errorData?.errorTitle || 'No pudimos procesar tu pago'}</ErrorTitle>
          </ErrorHeader>
          <ErrorInstruction>{errorData?.errorInstruction || 'Revisa la información de tu tarjeta'}</ErrorInstruction>
          <ErrorDetails>Error:<strong> {errorData?.errorCode || ''}</strong> - {errorData?.errorMessage || 'Ha ocurrido un error'}</ErrorDetails>
        </ErrorContentBox>
        <ButtonContainer>
          <button className="payment-button" onClick={handleRetry}>
            Intentar de nuevo
            <Iconify icon="material-symbols:refresh-rounded" size={20} />
          </button>
        </ButtonContainer>
      </ModalContent>
    </ModalPanel>,
    node
  );
};

const ModalContent = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  background-color: #202020;
  border: 1px solid #404040;
  border-radius: 8px;
  padding: 24px;
  margin-top: 3em;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #404040;
  background-color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  svg {
    color: #202020;
    width: 18px;
    height: 18px;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const ErrorContentBox = styled.div`
  background-color: #1A1A1A;
  border: 1px solid #E04040;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 50px;
`;

const ErrorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WarningIcon = styled.div`
  color: #FFC107;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 2rem;
`;

const ErrorTitle = styled.h3`
  color: #FFFFFF;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
`;

const ErrorInstruction = styled.p`
  color: #FFFFFF;
  font-size: 1.4rem;
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
`;

const ErrorDetails = styled.p`
  color: #FFFFFF;
  font-size: 1.2rem;
  font-weight: 400;
  margin: 0;
  line-height: 1.5;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  

`;

